import axios from 'axios';
import Cookies from 'js-cookie';
import { ChangeEvent, FC, SyntheticEvent, useRef, useState } from 'react';
import { useLocation } from 'react-use';

import Flex from 'quarks/Flex';
import Link from 'quarks/Link';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import TextField from 'molecules/TextField/TextField';

import ComponentButton from 'components/Button/ComponentButton';

import { validateEmail } from 'utils/functions';

import type { ContentfulComponentForm } from 'graphqlTypes';

interface FormProps
  extends FlexProps,
    Omit<ContentfulComponentForm, 'id' | 'children' | 'contentful_id' | 'internal' | 'node_locale'> {
  backgroundIsDark?: boolean;
  buttonLabel?: string;
  onSuccess?: () => void;
}

const BasicForm: FC<FormProps> = ({ buttonLabel, onSuccess }) => {
  const [hasTyped, setHasTyped] = useState(false);
  const [validated, setValidated] = useState<boolean>(false);
  const { href } = useLocation();
  const portalId = '3381425';
  const formId = '38049a11-5dfd-4098-99a0-e6453e88f7ef';
  const formRef = useRef<HTMLFormElement>(null);
  const [demo, setDemo] = useState({
    email: '',
    hp123: '',
    url: href,
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDemo({ ...demo, [e.target.name]: e.target.value });
    if (!hasTyped) {
      setHasTyped(true);
    }
  };

  const handleBlur = () => {
    setValidated(!(demo.email && validateEmail(demo.email)));
  };

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!(demo.email && validateEmail(demo.email))) {
      setValidated(true);

      return;
    }

    const honeypot = formRef?.current?.querySelector('[name="hp123"]');
    if (honeypot?.value) {
      return;
    }

    setValidated(false);

    try {
      demo.url = href;
      const url = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;
      const fieldsData: { name: string; value: any }[] = [];

      (Object.keys(demo) as (keyof typeof demo)[]).map(key => {
        if (demo[key] != null) {
          fieldsData.push({ name: key, value: demo[key] });
        }

        return;
      });
      const postData = {
        fields: fieldsData,
      };
      axios
        .post(url, postData, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then(() => {
          setSubmitted(true);
          Cookies.set('email', demo.email);
          onSuccess && onSuccess();
        })
        .catch((err: any) => console.log(err));
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Flex flexDirection="column" width="100%">
      {submitted ? (
        <Text textColor="common-white">Thank you for submitting</Text>
      ) : (
        <form noValidate ref={formRef}>
          <Flex flexDirection="column" gap="16px" md={{ flexDirection: 'row' }}>
            <TextField
              type="email"
              placeholder="Enter your Email"
              name="email"
              width="100%"
              required
              onChange={handleChange}
              onBlur={handleBlur}
              css={{
                width: '100%',
                maxWidth: '100%',
                minWidth: '100%',
                '&& .MuiInputBase-root': {
                  maxWidth: '100%',
                  minWidth: '100%',
                  border: validated ? '1px solid #f00' : '1px solid #D0D5DD',
                },
              }}
            />
            <TextField type="text" name="hp123" width="100%" display="none" onChange={handleChange} />
            <Flex
              flexDirection="column"
              onClick={handleSubmit}
              css={`
                .MuiButton-root {
                  max-width: 100% !important;
                  min-width: max-content;
                  width: 100% !important;
                  min-height: 48px !important;
                },
              `}
            >
              <ComponentButton label={buttonLabel || 'Book a demo'} isButton />
            </Flex>
          </Flex>
          <Flex
            className="privacy-policy"
            transition="opacity ease-in 0.5s"
            textColor="common-white"
            fontSize="textXs"
            {...(hasTyped
              ? { visibility: 'visible', opacity: 1, marginTop: 16 }
              : { height: 0, visibility: 'hidden', opacity: 0 })}
          >
            <p>
              FieldRoutes needs the contact information you provide to us to contact you about our products and
              services. You may unsubscribe from these communications at any time. For information on how to
              unsubscribe, as well as our privacy practices and commitment to protecting your privacy, please review our{' '}
              <Link textColor="common-white" href="/privacy-policy">
                Privacy Policy
              </Link>
              .
            </p>
          </Flex>
        </form>
      )}
    </Flex>
  );
};

export default BasicForm;
